import type { Editor, Range, TiptapEditorProps } from 'OpalChat/TiptapEditorTypes';
import React, { Suspense, useEffect, useMemo, useRef } from 'react';
import { Box, Skeleton } from '@optiaxiom/react';

// Fallback component when TiptapEditor fails to load
const FallbackEditor: React.FC<TiptapEditorProps> = () => (
  <Box p="12" border="1" borderColor="border.default" rounded="md">
    TiptapEditor failed to load. Please refresh the page.
  </Box>
);

// Lazy load TiptapEditor from the OpalChat remote
const TiptapEditor = React.lazy<React.ComponentType<TiptapEditorProps>>(() =>
  import('OpalChat/TiptapEditor')
    .then(module => ({
      default: module.TiptapEditor,
    }))
    .catch(error => {
      console.error('Failed to load OpalChat TiptapEditor component:', error);
      return {
        default: FallbackEditor,
      };
    })
);

type Selectable = {
  name: string;
  description?: string | null;
};

const VARIABLE_PATTERN = '\\[\\[(.*?)\\]\\]';

type TiptapEditorWrapperProps = {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  selectables?: Selectable[];
};

const TiptapEditorWrapper = ({
  value = '',
  onChange,
  placeholder = '',
  className,
  selectables = [],
}: TiptapEditorWrapperProps) => {
  const selectablesRef = useRef(selectables);

  useEffect(() => {
    selectablesRef.current = selectables;
  }, [selectables]);

  const commands = useMemo(() => {
    return [
      {
        char: '/',
        items: (query: string) => {
          return selectablesRef.current
            .filter(selectable => selectable.name.toLowerCase().includes(query.toLowerCase()))
            .map(selectable => ({
              id: selectable.name,
              label: selectable.name,
              description: selectable.description || '',
              command: ({ editor, range }: { editor: Editor; range: Range }) => {
                editor
                  .chain()
                  .focus()
                  .deleteRange(range)
                  .insertContent(`[[${selectable.name}]]`)
                  .run();
              },
            }));
        },
      },
    ];
  }, []);

  return (
    <Suspense fallback={<Skeleton h="56" />}>
      <Box
        w="full"
        border="1"
        borderColor="border.default"
        rounded="md"
        bg="bg.default"
        p="12"
        overflow="auto"
        style={{ minHeight: '200px' }}
        className={className}
      >
        <TiptapEditor
          content={value}
          onChange={onChange}
          commands={commands}
          isReadOnly={false}
          placeholder={placeholder}
          patternsToHighlight={[VARIABLE_PATTERN]}
          enableDragging
        />
      </Box>
    </Suspense>
  );
};

export { TiptapEditorWrapper };
